import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { Dashboard } from '../../../features/dashboard/models/dashboard.model';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import type { DashboardExportDto } from '@anessa/shared-models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { uniqueDashboardNameValidator } from '../unique-dashboard-name.validator';
import { DashboardService } from '../../../features/dashboard/services/dashboard/dashboard.service';
import { Project } from '../../../shared/services/project/project.model';

@Component({
  selector: 'app-add-dashboard-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    I18nPipe,
    I18nScopeReadyDirective,
  ],
  providers: [
    provideI18nScope('ui/dashboard/add-dashboard-dialog'),
    I18nScopedService,
  ],
  templateUrl: './add-dashboard-dialog.html',
  styleUrl: './add-dashboard-dialog.scss',
})
export class AddDashboardDialog {
  readonly dashboard = Dashboard.createEmpty();

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
  });

  importedFileName = '';
  importError = '';
  importIssues: string[] = [];

  data = inject(MAT_DIALOG_DATA) as {
    isOverview: boolean;
    currentProject: Project;
  };
  private readonly dashboardService = inject(DashboardService);
  private readonly dialogRef = inject(MatDialogRef<AddDashboardDialog>);
  private readonly i18n = inject(I18nScopedService);

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.importError = '';
    this.importedFileName = '';
    this.importIssues = [];

    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as DashboardExportDto;

      if (!this.isValidExport(parsed)) {
        this.importError = this.i18n.t('import.invalid_msg');
        return;
      }

      this.dashboard.items = parsed.items;
      this.dashboard.isOverview = parsed.isOverview ?? this.data.isOverview;
      this.dashboard.projectId = parsed.projectId;
      this.dashboard.plantId = parsed.plantId;
      const importLog = this.dashboard.cleanupForImport(
        this.data.currentProject,
      );
      this.importIssues = importLog.getImportIssues();

      this.importedFileName = file.name;

      if (parsed.name) {
        this.form.controls.name.setValue(parsed.name);
      }
    } catch (e) {
      console.log(e);
      this.importIssues = [];
      this.importError = this.i18n.t('import.invalid_format');
    }
  }

  private isValidExport(data: unknown): data is DashboardExportDto {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    if (!Array.isArray(obj['items'])) return false;

    return (obj['items'] as unknown[]).every((item) =>
      this.isValidLayoutItem(item),
    );
  }

  private isValidLayoutItem(item: unknown): boolean {
    if (!item || typeof item !== 'object') return false;
    const li = item as Record<string, unknown>;
    return (
      typeof li['id'] === 'string' &&
      typeof li['x'] === 'number' &&
      typeof li['y'] === 'number' &&
      typeof li['cols'] === 'number' &&
      typeof li['rows'] === 'number' &&
      typeof li['widgetType'] === 'string'
    );
  }

  constructor() {
    this.form.controls.name.addValidators(
      uniqueDashboardNameValidator(this.dashboardService.dashboardList),
    );

    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (!this.form.controls.name.touched)
          this.form.controls.name.markAsTouched();
        this.form.controls.name.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });
  }

  apply(): void {
    if (this.form.invalid) return;

    this.dashboard.isOverview = this.data.isOverview;
    this.dashboard.name = this.form.controls.name.value ?? '';
    this.dashboard.projectId = this.data.currentProject.id;
    if (this.data.currentProject.plantMeta) {
      this.dashboard.plantId = this.data.currentProject.plantMeta.id;
    }
    this.dialogRef.close(this.dashboard);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
